import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { AcceptPaymentDialog } from "@/components/finance/AcceptPaymentDialog";
import { fetchStudent } from "@/api/students";
import { STUDENT_STATUS_LABELS } from "@/types/enums";
import { useAuth } from "@/auth/useAuth";
import { StudentProfileTab } from "./tabs/StudentProfileTab";
import { StudentEnrollmentsTab } from "./tabs/StudentEnrollmentsTab";
import { StudentBillingTab } from "./tabs/StudentBillingTab";
import { StudentLessonsTab } from "./tabs/StudentLessonsTab";
import { StudentAttendanceTab } from "./tabs/StudentAttendanceTab";
import { StudentNotificationsTab } from "./tabs/StudentNotificationsTab";
import { StudentCreateDialog } from "./StudentCreateDialog";

export function StudentDetailPage() {
  const { id = "" } = useParams();
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const { hasPermission } = useAuth();

  const { data: student, isLoading, error } = useQuery({
    queryKey: ["student", id],
    queryFn: () => fetchStudent(id),
    enabled: Boolean(id),
  });

  return (
    <>
      <PageHeader
        title={student ? student.fullName : "Ученик"}
        subtitle={student ? `Телефон: ${student.phone}` : undefined}
        actions={
          student && (
            <Stack direction="row" spacing={1} alignItems="center">
              <StatusChip label={STUDENT_STATUS_LABELS[student.status]} />
              {hasPermission("students.edit") && (
                <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>
                  Редактировать
                </Button>
              )}
              {hasPermission("fin.payments") && (
                <Button size="small" variant="contained" onClick={() => setPayOpen(true)}>
                  Принять оплату
                </Button>
              )}
            </Stack>
          )
        }
      />
      <QueryState isLoading={isLoading} error={error}>
        {student && (
          <Paper className="crm-detail-panel" variant="outlined">
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false}>
              <Tab label="Анкета" />
              <Tab label="Курсы и группы" />
              <Tab label="Оплаты по пакетам" />
              <Tab label="Уроки и оценки" />
              <Tab label="Посещаемость" />
              <Tab label="Уведомления" />
            </Tabs>
            <Box className="crm-detail-panel__body" p={3}>
              {tab === 0 && <StudentProfileTab student={student} />}
              {tab === 1 && <StudentEnrollmentsTab studentId={student.id} />}
              {tab === 2 && <StudentBillingTab studentId={student.id} />}
              {tab === 3 && <StudentLessonsTab studentId={student.id} />}
              {tab === 4 && <StudentAttendanceTab studentId={student.id} />}
              {tab === 5 && <StudentNotificationsTab studentId={student.id} />}
            </Box>
          </Paper>
        )}
      </QueryState>
      {student && <StudentCreateDialog open={editOpen} onClose={() => setEditOpen(false)} student={student} />}
      {student && <AcceptPaymentDialog open={payOpen} onClose={() => setPayOpen(false)} initialStudent={student} />}
    </>
  );
}
